import React, { useState, useRef, useEffect } from 'react';
import {
    HiViewList,
    HiCollection,
    HiDownload,
    HiUpload
} from 'react-icons/hi';
import ResumoTab from './ResumoTab';
import StatisticsTab from './StatisticsTab';
import DistributionsTab from './DistributionsTab';
import SimulationTab from './SimulationTab';
import ExportModal from './ExportModal';
import apiService from '../services/apiService';

const ResultsPanel = ({ results, isLoading, loadingMessage = 'Processando análise...', isModifyingProjects, setIsModifyingProjects, error, appliedFilters, onImport, onDataUpdate, dataReferencia, setDataReferencia }) => {
    const [activeTab, setActiveTab] = useState('resumo');
    const [showExportModal, setShowExportModal] = useState(false);

    // Estados de seleção de projetos (elevados para persistir entre abas)
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [tableSearchTerm, setTableSearchTerm] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalType, setConfirmModalType] = useState('remove');
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertModalData, setAlertModalData] = useState({ type: 'success', message: '' });

    // Estados para TODOS os projetos disponíveis (carregado sempre que filtros são aplicados)
    // projetosMap: {id: nome} - dicionário com todos os projetos
    const [projetosMap, setProjetosMap] = useState({});
    const [todosProjetosDisponiveis, setTodosProjetosDisponiveis] = useState([]);

    const fileInputRef = useRef(null);

    // Carregar TODOS os projetos disponíveis sempre que os filtros forem aplicados
    useEffect(() => {
        const carregarTodosProjetos = async () => {
            // Só buscar projetos se os filtros foram aplicados
            if (!appliedFilters) {
                return;
            }

            try {
                const response = await apiService.obterTodosProjetos(appliedFilters);
                if (response.sucesso && response.projetos) {
                    // response.projetos agora é um dicionário {id: nome}
                    setProjetosMap(response.projetos);
                    // Extrair apenas os IDs (chaves) para a lista de projetos disponíveis
                    setTodosProjetosDisponiveis(Object.keys(response.projetos));
                }
            } catch (error) {
                console.error('Erro ao carregar todos os projetos:', error);
            }
        };

        carregarTodosProjetos();
    }, [appliedFilters]); // Executa sempre que appliedFilters mudar

    const tabs = [
        { id: 'resumo', label: 'Resumo', icon: HiViewList },
        { id: 'statistics', label: 'Estatística descritiva', icon: HiCollection },
        { id: 'distributions', label: 'Distribuições', icon: HiCollection },
        { id: 'simulation', label: 'Simulação', icon: HiCollection }
    ];

    const handleExport = () => {
        setShowExportModal(true);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const importedData = JSON.parse(text);

            if (onImport) {
                onImport(importedData);
            }
        } catch (error) {
            alert('Erro ao importar arquivo JSON. Verifique se o arquivo é válido.');
        }

        event.target.value = '';
    };

    const renderTabContent = () => {
        if (isLoading || isModifyingProjects) {
            return (
                <div className="h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-lg text-gray-500">{isModifyingProjects ? 'Processando análise...' : loadingMessage}</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="h-full bg-gray-50 flex items-center justify-center">
                    <div className="text-center">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-red-300" />
                        <p className="text-lg text-red-600">Falha: {error}</p>
                    </div>
                </div>
            );
        }

        if (!results || Object.keys(results).length === 0) {
            return (
                <div className="h-screen bg-gray-50 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg">Selecione os filtros e clique em "Aplicar filtros" para visualizar os resultados</p>
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'resumo':
                return <ResumoTab
                    data={results}
                    onDataUpdate={onDataUpdate}
                    appliedFilters={appliedFilters}
                    isModifyingProjects={isModifyingProjects}
                    setIsModifyingProjects={setIsModifyingProjects}
                    selectionMode={selectionMode}
                    setSelectionMode={setSelectionMode}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    tableSearchTerm={tableSearchTerm}
                    setTableSearchTerm={setTableSearchTerm}
                    showConfirmModal={showConfirmModal}
                    setShowConfirmModal={setShowConfirmModal}
                    confirmModalType={confirmModalType}
                    setConfirmModalType={setConfirmModalType}
                    showAlertModal={showAlertModal}
                    setShowAlertModal={setShowAlertModal}
                    alertModalData={alertModalData}
                    setAlertModalData={setAlertModalData}
                    todosProjetosDisponiveis={todosProjetosDisponiveis}
                    projetosMap={projetosMap}
                />;
            case 'statistics':
                return <StatisticsTab data={results} />;
            case 'distributions':
                return <DistributionsTab data={results} />;
            case 'simulation':
                return (
                    <SimulationTab
                        data={results}
                        dataReferencia={dataReferencia}
                        setDataReferencia={setDataReferencia}
                    />
                );
            default:
                return <ResumoTab
                    data={results}
                    onDataUpdate={onDataUpdate}
                    appliedFilters={appliedFilters}
                    isModifyingProjects={isModifyingProjects}
                    setIsModifyingProjects={setIsModifyingProjects}
                    selectionMode={selectionMode}
                    setSelectionMode={setSelectionMode}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    tableSearchTerm={tableSearchTerm}
                    setTableSearchTerm={setTableSearchTerm}
                    showConfirmModal={showConfirmModal}
                    setShowConfirmModal={setShowConfirmModal}
                    confirmModalType={confirmModalType}
                    setConfirmModalType={setConfirmModalType}
                    showAlertModal={showAlertModal}
                    setShowAlertModal={setShowAlertModal}
                    alertModalData={alertModalData}
                    setAlertModalData={setAlertModalData}
                    todosProjetosDisponiveis={todosProjetosDisponiveis}
                    projetosMap={projetosMap}
                />;
        }
    };

    return (
        <div className="flex-1 bg-white h-full overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 h-20 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center">
                    <span className="text-lg font-semibold">Resultados</span>
                    <HiViewList className="w-5 h-5 ml-2" />
                </div>

                {/* <button
                    style={{ border: "1px solid black" }}
                    onClick={handleImportClick}
                    disabled={isLoading || isModifyingProjects}
                    className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                    <HiUpload className="w-4 h-4" />
                    <span>Importar</span>
                </button> */}

            </div>

            {/* Container para Tabs e Content com overlay */}
            <div className="flex-1 flex flex-col overflow-hidden" style={{ position: 'relative' }}>
                {/* Overlay para bloquear interação durante processamento */}
                {isModifyingProjects && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            zIndex: 9999,
                            cursor: 'not-allowed'
                        }}
                    />
                )}

                {/* Tabs */}
                {results && Object.keys(results).length > 0 && (
                    <div className="border-b border-gray-200 flex-shrink-0">
                        <div className="flex justify-between items-center">
                            <nav className="flex space-x-0" aria-label="Tabs">
                                {tabs.map((tab) => {
                                    const Icon = tab.icon;
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`${activeTab === tab.id
                                                ? 'border-blue-500 text-blue-600 bg-blue-50'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                                } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors`}
                                        >
                                            <Icon className="w-4 h-4" />
                                            <span>{tab.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>

                            {/* Export Button */}
                            <div className="px-6 py-4">
                                <button
                                    onClick={handleExport}
                                    style={{ backgroundColor: "#003B6B" }}
                                    className="flex items-center space-x-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    <HiDownload className="w-4 h-4" />
                                    <span>Exportar</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden">
                    <div className="h-full w-full overflow-hidden">
                        {renderTabContent()}
                    </div>
                </div>
            </div>


            {/* Export Modal */}
            {showExportModal && (
                <ExportModal
                    results={results}
                    appliedFilters={appliedFilters}
                    activeTab={activeTab}
                    dataReferencia={dataReferencia}
                    onClose={() => setShowExportModal(false)}
                />
            )}

            {/* Hidden file input for import */}
            <input
                ref={fileInputRef}
                type="file"
                accept="application/json"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default ResultsPanel;
